import React from 'react';

import { DevContext } from './../../ricos-context/index.js';
import { logInvariant } from './../log-invariant.js';
import { createContext, useContext } from './../use-context-selector.js';

import { type DecodedInstance, parseInstance } from './parse-instance.js';
import { type GetWixClientPromise, useGetWixClientPromise } from './use-get-wix-client-promise.js';

export type WixInstanceState = Partial<DecodedInstance> & {
  getWixClientPromise: GetWixClientPromise;
};

export const WixInstanceContext = createContext<WixInstanceState>({} as WixInstanceState);

export const WixInstanceContextProvider: React.FC<{
  getWixClientPromise?: GetWixClientPromise;
  children: React.ReactNode;
  jsdomMode: boolean | undefined;
  instance: string | undefined;
}> = ({ children, getWixClientPromise: existingGetWixClientPromise, jsdomMode, instance }) => {
  const { getWixClientPromise } = useGetWixClientPromise('', existingGetWixClientPromise);
  const [state, setState] = React.useState<WixInstanceState>(() => ({
    ...(instance ? parseInstance(instance) : {}),
    getWixClientPromise,
  }));

  const devContext = React.useContext(DevContext);

  React.useEffect(() => {
    if (jsdomMode) {
      if (devContext.decodedInstance) {
        setState(prev => ({
          ...prev,
          ...devContext.decodedInstance,
        }));
      }
      return;
    }

    const decode = async () => {
      try {
        if (instance) {
          return;
        }

        const wixClient = await getWixClientPromise();
        const { headers } = await wixClient.auth.getAuthHeaders();
        const authorizationHeader = headers.Authorization;

        setState(prev => ({
          ...prev,
          ...(authorizationHeader ? parseInstance(authorizationHeader) : {}),
        }));
      } catch (error) {
        logInvariant(new Error(`Failed to decode instance: ${error}`));
        setState(prev => ({ ...prev, getWixClientPromise }));
      }
    };

    decode();
  }, [instance, getWixClientPromise, jsdomMode, devContext.decodedInstance]);

  return <WixInstanceContext.Provider value={state}>{children}</WixInstanceContext.Provider>;
};

export type WixInstanceService = () => WixInstanceState;

export const useWixInstanceService = (): WixInstanceService => {
  const context = useContext(WixInstanceContext);
  if (!context) {
    throw new Error('useWixInstanceService must be used within a WixInstanceContextProvider');
  }
  const serviceRef = React.useRef<WixInstanceState>(context);
  serviceRef.current = context;
  return React.useCallback(() => serviceRef.current, []);
};
